import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Search, Loader2, User, ArrowRight, Calendar, ChevronRight } from 'lucide-react';
import { API } from '../../App';

const AdminTPIPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await API.get('/admin/clients');
        // API returns array directly, not { clients: [...] }
        setClients(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error('Failed to fetch clients:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  const filteredClients = clients.filter(client =>
    (client.full_name || client.email || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#BC562B]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-tpi-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Activity className="w-8 h-8 text-[#BC562B]" />
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A]">TPI Physical Screenings</h1>
        </div>
        <p className="text-[#57534E]">
          Titleist Performance Institute physical screening assessments. Select a client to create or view their TPI screening.
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-[#2C5530] text-white p-6 mb-8 rounded-lg">
        <h2 className="text-lg font-semibold mb-2">Body-Swing Connection</h2>
        <p className="text-white/80 text-sm">
          The TPI Physical Screen identifies how physical limitations affect swing mechanics. 
          There is no "one way" to swing — only the most efficient way for each golfer's body.
          Use these screenings to create personalized S&C prescriptions for your clients.
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29E]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clients..."
            className="w-full pl-10 pr-4 py-3 border border-[#E7E5E4] rounded-lg focus:outline-none focus:border-[#BC562B] transition-colors"
            data-testid="tpi-client-search"
          />
        </div>
      </div>

      {/* Client List */}
      <div className="bg-white border border-[#E7E5E4] rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E7E5E4] bg-[#F9F7F4]">
          <h3 className="font-semibold text-[#1A1A1A]">Select a Client</h3>
        </div>
        
        {filteredClients.length === 0 ? (
          <div className="p-8 text-center text-[#57534E]">
            {search ? 'No clients found matching your search.' : 'No clients available.'}
          </div>
        ) : (
          <div className="divide-y divide-[#E7E5E4]">
            {filteredClients.map((client) => (
              <Link
                key={client.id}
                to={`/coach/clients/${client.id}/tpi`}
                className="flex items-center justify-between p-4 hover:bg-[#F9F7F4] transition-colors group"
                data-testid={`tpi-client-${client.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#F2F0E9] rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-[#57534E]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{client.full_name || 'Unnamed Client'}</p>
                    <p className="text-sm text-[#57534E]">{client.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#BC562B] opacity-0 group-hover:opacity-100 transition-opacity">
                    Start TPI Screen
                  </span>
                  <ChevronRight className="w-5 h-5 text-[#A8A29E] group-hover:text-[#BC562B] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E7E5E4] rounded-lg p-6">
          <p className="text-3xl font-bold text-[#1A1A1A]">{clients.length}</p>
          <p className="text-sm text-[#57534E]">Total Clients</p>
        </div>
        <div className="bg-white border border-[#E7E5E4] rounded-lg p-6">
          <p className="text-3xl font-bold text-[#2C5530]">5</p>
          <p className="text-sm text-[#57534E]">TPI Test Categories</p>
        </div>
        <div className="bg-white border border-[#E7E5E4] rounded-lg p-6">
          <p className="text-3xl font-bold text-[#BC562B]">16</p>
          <p className="text-sm text-[#57534E]">Individual Tests</p>
        </div>
      </div>
    </div>
  );
};

export default AdminTPIPage;
